import { useState } from 'react';
import { ReportStatus } from '../../../types';
import { Messages } from '../../utils/message-constants';
import { ToolsRoles } from '../../utils/roles/client-roles';
import RunPolicyButton from './run-policy-button';
import RunningPolicyButton from './running-policy-button';

import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Delete from '@mui/icons-material/Delete';
import LibraryAdd from '@mui/icons-material/LibraryAdd';
import Edit from '@mui/icons-material/Edit';
import MoreVert from '@mui/icons-material/MoreVert';

interface PolicyActionsCellProps {
  policyStatus: ReportStatus;
  canRun: boolean;
  isNewUser: boolean;
  onRun: (e: React.MouseEvent) => void;
  canEdit: boolean;
  canClone: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onClone: () => void;
  onDelete: () => void;
}

export default function PolicyActionsCell({
  policyStatus,
  canRun,
  isNewUser,
  onRun,
  canEdit,
  canClone,
  canDelete,
  onEdit,
  onClone,
  onDelete,
}: PolicyActionsCellProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const runButton =
    policyStatus === 'RUNNING' ? (
      <RunningPolicyButton onClick={(e) => e.stopPropagation()} />
    ) : (
      <Tooltip
        arrow
        title={
          !canRun
            ? Messages.Errors.Permission.Acting(ToolsRoles.MaunalRunPolicy.name)
            : isNewUser
            ? "Can't manually run New User Creation policies"
            : 'Run Policy'
        }
      >
        <span style={{ display: 'inline-flex', pointerEvents: 'auto' }}>
          <RunPolicyButton
            disabled={!canRun || isNewUser}
            onClick={(e) => {
              e.stopPropagation();
              onRun(e);
            }}
          />
        </span>
      </Tooltip>
    );

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      gap={0.5}
      sx={{ width: '100%' }}
    >
      {runButton}
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setMenuAnchor(e.currentTarget);
        }}
      >
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          disabled={!canEdit}
          onClick={() => {
            setMenuAnchor(null);
            onEdit();
          }}
        >
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem
          disabled={!canClone}
          onClick={() => {
            setMenuAnchor(null);
            onClone();
          }}
        >
          <ListItemIcon>
            <LibraryAdd fontSize="small" />
          </ListItemIcon>
          Clone
        </MenuItem>
        <MenuItem
          disabled={!canDelete}
          onClick={() => {
            setMenuAnchor(null);
            onDelete();
          }}
        >
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>
    </Stack>
  );
}
